/**
 * Postal Code Persistence Helper
 * Manages postal code storage, retrieval, and formatting across the application
 */

const POSTAL_CODE_KEY = "userPostalCode";

/**
 * Format a postal code with proper capitalization and spacing
 * For Canadian postal codes (A1A 1A1): uppercase with space after first 3 chars
 * For US ZIP codes: just returns the value as-is (5 or 9 digits)
 * @param {string} value - The raw postal code input
 * @returns {string} The formatted postal code
 */
export const formatPostalCode = (value) => {
  if (!value) return "";

  // Remove any existing spaces and convert to uppercase
  const cleaned = value.replace(/\s/g, "").toUpperCase();

  // Limit to 6 characters for Canadian postal codes
  const limited = cleaned.slice(0, 6);

  // Add space after third character if there are more than 3 characters
  // This handles Canadian postal codes (A1A 1A1 format)
  if (limited.length > 3 && /^[A-Z]/.test(limited)) {
    return limited.slice(0, 3) + " " + limited.slice(3);
  }

  return limited;
};

export const postalCodeHelper = {
  /**
   * Save postal code to sessionStorage
   * @param {string} postalCode - The postal code to save
   */
  savePostalCode: (postalCode) => {
    if (postalCode && postalCode.trim()) {
      sessionStorage.setItem(POSTAL_CODE_KEY, postalCode.trim());
      console.log("✅ Postal code saved:", postalCode);
    }
  },

  /**
   * Get postal code from sessionStorage
   * @returns {string|null} The stored postal code or null
   */
  getPostalCode: () => {
    const stored = sessionStorage.getItem(POSTAL_CODE_KEY);
    if (stored) {
      console.log("📍 Retrieved postal code from storage:", stored);
    }
    return stored;
  },

  /**
   * Clear postal code from sessionStorage
   */
  clearPostalCode: () => {
    sessionStorage.removeItem(POSTAL_CODE_KEY);
    console.log("🗑️ Postal code cleared from storage");
  },

  /**
   * Check if a postal code exists in storage
   * @returns {boolean}
   */
  hasPostalCode: () => {
    const postalCode = sessionStorage.getItem(POSTAL_CODE_KEY);
    return postalCode !== null && postalCode.trim().length > 0;
  },

  /**
   * Get postal code with fallback priority
   * @param {string} stateZip - Postal code from navigation state
   * @returns {string|null} The postal code to use
   */
  getPostalCodeWithFallback: (stateZip) => {
    if (stateZip && stateZip.trim()) {
      // Save state zip to storage for future use
      postalCodeHelper.savePostalCode(stateZip);
      return stateZip;
    }

    // Fallback to stored postal code
    return postalCodeHelper.getPostalCode();
  },
};

export default postalCodeHelper;
